// EditProfilePage.tsx

import { useState, useEffect, useRef } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useAuth } from "../../lib/AuthContext"
import { supabase } from "../../lib/supabase"
import BaseCard from "../common/layout/BaseCard"
import { AvatarUpload } from "../common/ui/AvatarUpload"
import {
  getSectors,
  getSubsectorsBySector,
  getPlatforms,
  getTools,
  getProgrammingLanguages,
  getCountries,
  getJobFunctions,
  getIndustries,
  type TaxonomyTerm,
  type Sector,
} from "../../lib/taxonomyService"
import { Button } from "../common/ui/Button"
import { captureAppException } from '../../utils/errorCaptureUtils'

interface UserProfile {
  id: string
  first_name: string
  last_name: string
  profile_image_url: string | null
  about: string | null
  email: string
  phone: string | null
  job_title: string | null
  organization: string | null
  city: string | null
  country_id: string | null
  job_function_id: string | null
  industry_id: string | null
  social_links: {
    facebook?: string
    twitter?: string
    linkedin?: string
  }
}

interface UserTaxonomies {
  sectors: string[]
  subsectors: string[]
  platforms: string[]
  tools: string[]
  programming_languages: string[]
}

export const EditProfilePage = () => {
  const navigate = useNavigate()
  const { user, refreshUserMetadata } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [userTaxonomies, setUserTaxonomies] = useState<UserTaxonomies>({
    sectors: [],
    subsectors: [],
    platforms: [],
    tools: [],
    programming_languages: []
  })

  const [sectors, setSectors] = useState<Sector[]>([])
  const [allSubsectors, setAllSubsectors] = useState<TaxonomyTerm[]>([])
  const [platforms, setPlatforms] = useState<TaxonomyTerm[]>([])
  const [tools, setTools] = useState<TaxonomyTerm[]>([])
  const [programmingLanguages, setProgrammingLanguages] = useState<TaxonomyTerm[]>([])
  const [countries, setCountries] = useState<TaxonomyTerm[]>([])
  const [jobFunctions, setJobFunctions] = useState<TaxonomyTerm[]>([])
  const [industries, setIndustries] = useState<TaxonomyTerm[]>([])

  const [taxonomiesLoaded, setTaxonomiesLoaded] = useState(false)

  const hasLoadedTaxonomies = useRef(false)
  const hasFetchedProfile = useRef(false)

  useEffect(() => {
    if (user && !hasLoadedTaxonomies.current) {
      loadTaxonomies()
      hasLoadedTaxonomies.current = true
    }
  }, [user])

  const loadTaxonomies = async () => {
    try {
      const [
        sectorsData,
        platformsData,
        toolsData,
        languagesData,
        countriesData,
        jobFunctionsData,
        industriesData,
      ] = await Promise.all([
        getSectors(),
        getPlatforms(),
        getTools(),
        getProgrammingLanguages(),
        getCountries(),
        getJobFunctions(),
        getIndustries(),
      ])

      setSectors(sectorsData)
      setPlatforms(platformsData)
      setTools(toolsData)
      setProgrammingLanguages(languagesData)
      setCountries(countriesData)
      setJobFunctions(jobFunctionsData)
      setIndustries(industriesData)

      // Get all subsectors for all sectors
      const subsectorPromises = sectorsData.map((sector) => getSubsectorsBySector(sector.id))
      const subsectorResults = await Promise.all(subsectorPromises)
      setAllSubsectors(subsectorResults.flat())

      setTaxonomiesLoaded(true)
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'edit_profile',
          user_id: user?.id,
          action: 'load_taxonomies'
        }
      })
      console.error("Error loading taxonomies:", err)
      setError("Failed to load form options")
    }
  }

  useEffect(() => {
    if (user && taxonomiesLoaded && !hasFetchedProfile.current) {
      fetchProfile()
      hasFetchedProfile.current = true
    }
  }, [user, taxonomiesLoaded])

  const fetchProfile = async () => {
    try {
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(`
          *,
          user_sectors(sector_id),
          user_subsectors(subsector_id),
          user_platforms(platform_id),
          user_tools(tool_id),
          user_programming_languages(programming_language_id)
        `)
        .eq("id", user?.id)
        .single()

      if (userError) throw userError

      // Pull existing taxonomy data
      const taxonomies: UserTaxonomies = {
        sectors: userData.user_sectors?.map((s: any) => s.sector_id) || [],
        subsectors: userData.user_subsectors?.map((s: any) => s.subsector_id) || [],
        platforms: userData.user_platforms?.map((p: any) => p.platform_id) || [],
        tools: userData.user_tools?.map((t: any) => t.tool_id) || [],
        programming_languages: userData.user_programming_languages?.map((l: any) => l.programming_language_id) || []
      }

      setUserTaxonomies(taxonomies)

      const mappedProfile: UserProfile = {
        id: userData.id,
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        profile_image_url: userData.profile_image_url,
        about: userData.about,
        phone: userData.phone,
        job_title: userData.job_title,
        organization: userData.organization,
        city: userData.city,
        country_id: userData.country_id,
        job_function_id: userData.job_function_id,
        industry_id: userData.industry_id,
        social_links: userData.social_links || {}
      }

      setProfile(mappedProfile)
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'edit_profile',
          user_id: user?.id,
          action: 'fetch_profile'
        }
      })
      setError(err instanceof Error ? err.message : "Failed to fetch profile data")
    } finally {
      setLoading(false)
    }
  }

  const handleSectorChange = (sectorId: string) => {
    if (!profile) return

    const newSectors = userTaxonomies.sectors.includes(sectorId)
      ? userTaxonomies.sectors.filter((s) => s !== sectorId)
      : [...userTaxonomies.sectors, sectorId]

    const validSubsectors = userTaxonomies.subsectors.filter((ss) =>
      allSubsectors.some((s) => s.sector_id && newSectors.includes(s.sector_id) && s.id === ss)
    )

    setUserTaxonomies({
      ...userTaxonomies,
      sectors: newSectors,
      subsectors: validSubsectors
    })
  }

  const handleSubsectorChange = (subsectorId: string) => {
    if (!profile) return

    const newSubsectors = userTaxonomies.subsectors.includes(subsectorId)
      ? userTaxonomies.subsectors.filter((s) => s !== subsectorId)
      : [...userTaxonomies.subsectors, subsectorId]

    setUserTaxonomies({
      ...userTaxonomies,
      subsectors: newSubsectors
    })
  }

  const handlePlatformChange = (platformId: string) => {
    const newPlatforms = userTaxonomies.platforms.includes(platformId)
      ? userTaxonomies.platforms.filter((p) => p !== platformId)
      : [...userTaxonomies.platforms, platformId]

    setUserTaxonomies({
      ...userTaxonomies,
      platforms: newPlatforms
    })
  }

  const handleToolChange = (toolId: string) => {
    const newTools = userTaxonomies.tools.includes(toolId)
      ? userTaxonomies.tools.filter((t) => t !== toolId)
      : [...userTaxonomies.tools, toolId]

    setUserTaxonomies({
      ...userTaxonomies,
      tools: newTools
    })
  }

  const handleProgrammingLanguageChange = (languageId: string) => {
    const newLanguages = userTaxonomies.programming_languages.includes(languageId)
      ? userTaxonomies.programming_languages.filter((l) => l !== languageId)
      : [...userTaxonomies.programming_languages, languageId]

    setUserTaxonomies({
      ...userTaxonomies,
      programming_languages: newLanguages
    })
  }

  const handleImageUpload = async (file: File) => {
    // Single path for each user's profile image
    const path = `${user!.id}/profile.png`

    try {
      const { error: updateError } = await supabase.storage
        .from("profile-images")
        .update(path, file, {
          cacheControl: "0",
          upsert: true
        })

      if (updateError) {
        // If update fails, try upload
        const { error: uploadError } = await supabase.storage
          .from("profile-images")
          .upload(path, file)
        if (uploadError) throw uploadError
      }

      // Get the public URL
      const {
        data: { publicUrl }
      } = supabase.storage.from("profile-images").getPublicUrl(path)

      const cachedUrl = `${publicUrl}?t=${Date.now()}`

      // Update user profile image
      setProfile((prevProfile) => {
        if (!prevProfile) return null
        return { ...prevProfile, profile_image_url: cachedUrl }
      })

      // Update user metadata
      const { error: updateMetaError } = await supabase.auth.updateUser({
        data: { profile_image_url: cachedUrl }
      })
      if (updateMetaError) {
        console.error("Failed to update metadata with new image:", updateMetaError)
      }

      // Ensure auth user metadata is refreshed for UI components like TopNav
      await refreshUserMetadata()

      // Persist to public.users immediately so the profile record stays in sync without requiring Save
      const { error: userRowError } = await supabase
        .from('users')
        .update({ profile_image_url: cachedUrl })
        .eq('id', user!.id)
      if (userRowError) {
        console.error('Failed to update users.profile_image_url:', userRowError)
      }

      // Update personal workspace logo
      const { data: workspaces, error: workspaceError } = await supabase
        .from('workspaces')
        .select('workspace_id')
        .eq('workspace_type', 'personal')
        .eq('workspace_id', (
          await supabase
            .from('workspace_users')
            .select('workspace_id')
            .eq('user_id', user!.id)
            .eq('role', 'owner')
            .single()
        ).data?.workspace_id)
        .single()

      if (workspaceError) {
        console.error('Error finding personal workspace:', workspaceError)
        return
      }

      if (workspaces) {
        const { error: logoError } = await supabase
          .from('workspaces')
          .update({ logo_url: cachedUrl })
          .eq('workspace_id', workspaces.workspace_id)

        if (logoError) {
          console.error('Error updating workspace logo:', logoError)
        }
      }
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'edit_profile',
          user_id: user?.id,
          action: 'upload_image'
        }
      })
      console.error("Image upload failed:", error)
      setError("Failed to upload profile image")
    }
  }

  const handleSave = async () => {
    if (!profile || !user?.id) return

    setSaving(true)
    setError(null)

    try {
      // Check if name has changed by comparing with original profile data
      const isNameChanged = 
        profile.first_name !== user.user_metadata?.first_name || 
        profile.last_name !== user.user_metadata?.last_name;

      // Update user metadata with new name and profile image
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          first_name: profile.first_name,
          last_name: profile.last_name,
          profile_image_url: profile.profile_image_url
        }
      });

      if (metadataError) throw metadataError;

      // Ensure UI reflects any metadata changes (name/avatar) immediately
      await refreshUserMetadata();

      // Update main user record
      const { error: profileError } = await supabase
        .from("users")
        .update(profile)
        .eq("id", user.id);
      if (profileError) throw profileError;

      // If name changed, update personal workspace name
      if (isNameChanged) {
        const fullName = `${profile.first_name} ${profile.last_name}`.trim();
        
        // First get the workspace_id of their personal workspace
        const { data: workspaceUser, error: workspaceUserError } = await supabase
          .from('workspace_users')
          .select('workspace_id, workspaces!inner(workspace_type)')
          .eq('user_id', user.id)
          .eq('role', 'owner')
          .eq('workspaces.workspace_type', 'personal')
          .single();

        if (workspaceUserError) {
          console.error('Error finding personal workspace:', workspaceUserError);
          return;
        }

        if (workspaceUser?.workspace_id) {
          const { error: workspaceError } = await supabase
            .from('workspaces')
            .update({ workspace_name: fullName })
            .eq('workspace_id', workspaceUser.workspace_id);

          if (workspaceError) {
            console.error('Error updating workspace name:', workspaceError);
          }
        }
      }

      // Clear existing user_sectors
      const { error: sectorsError } = await supabase
        .from("user_sectors")
        .delete()
        .eq("user_id", user.id)
      if (sectorsError) throw sectorsError

      // Insert new user_sectors
      if (userTaxonomies.sectors.length > 0) {
        const { error: insertSectorsError } = await supabase
          .from("user_sectors")
          .insert(
            userTaxonomies.sectors.map((sectorId) => ({
              user_id: user.id,
              sector_id: sectorId
            }))
          )
        if (insertSectorsError) throw insertSectorsError
      }

      // Clear user_subsectors
      const { error: subsectorsError } = await supabase
        .from("user_subsectors")
        .delete()
        .eq("user_id", user.id)
      if (subsectorsError) throw subsectorsError

      // Insert new user_subsectors
      if (userTaxonomies.subsectors.length > 0) {
        const { error: insertSubsectorsError } = await supabase
          .from("user_subsectors")
          .insert(
            userTaxonomies.subsectors.map((subsectorId) => ({
              user_id: user.id,
              subsector_id: subsectorId
            }))
          )
        if (insertSubsectorsError) throw insertSubsectorsError
      }

      // Clear user_platforms
      const { error: platformsError } = await supabase
        .from("user_platforms")
        .delete()
        .eq("user_id", user.id)
      if (platformsError) throw platformsError

      // Insert new user_platforms
      if (userTaxonomies.platforms.length > 0) {
        const { error: insertPlatformsError } = await supabase
          .from("user_platforms")
          .insert(
            userTaxonomies.platforms.map((platformId) => ({
              user_id: user.id,
              platform_id: platformId
            }))
          )
        if (insertPlatformsError) throw insertPlatformsError
      }

      // Clear user_tools
      const { error: toolsError } = await supabase
        .from("user_tools")
        .delete()
        .eq("user_id", user.id)
      if (toolsError) throw toolsError

      // Insert new user_tools
      if (userTaxonomies.tools.length > 0) {
        const { error: insertToolsError } = await supabase
          .from("user_tools")
          .insert(
            userTaxonomies.tools.map((toolId) => ({
              user_id: user.id,
              tool_id: toolId
            }))
          )
        if (insertToolsError) throw insertToolsError
      }

      // Clear user_programming_languages
      const { error: languagesError } = await supabase
        .from("user_programming_languages")
        .delete()
        .eq("user_id", user.id)
      if (languagesError) throw languagesError

      // Insert new user_programming_languages
      if (userTaxonomies.programming_languages.length > 0) {
        const { error: insertLanguagesError } = await supabase
          .from("user_programming_languages")
          .insert(
            userTaxonomies.programming_languages.map((langId) => ({
              user_id: user.id,
              programming_language_id: langId
            }))
          )
        if (insertLanguagesError) throw insertLanguagesError
      }

      navigate("/profile")
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'edit_profile',
          user_id: user?.id,
          action: 'save_profile'
        }
      })
      setError(err instanceof Error ? err.message : "Failed to save profile")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px]">
        <h1 className="text-2xl font-bold text-gray-900">Profile not found</h1>
        <Button onClick={() => navigate("/")} variant="outline" size="small" className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    )
  }

  return (
    <BaseCard>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
          <div className="flex gap-4 self-start">
            <Button variant="outline" size="small" onClick={() => navigate("/profile")}>
              Cancel
            </Button>
            <Button variant="filled" size="small" onClick={handleSave} disabled={saving}>
              Save
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="m-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <AvatarUpload
                currentImageUrl={profile.profile_image_url ?? undefined}
                onImageUpload={handleImageUpload}
                size="large"
                shape="circle"
                bucketName="profile-images"
                label="Profile Photo"
                helperText="PNG or JPG up to 10MB"
                outputWidth={512}
                outputHeight={512}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profile.first_name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s'-]/g, "")
                  setProfile({ ...profile, first_name: value })
                }}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profile.last_name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s'-]/g, "")
                  setProfile({ ...profile, last_name: value })
                }}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Short Bio</label>
              <textarea
                value={profile.about || ""}
                onChange={(e) => setProfile({ ...profile, about: e.target.value })}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              <input
                type="text"
                value={profile.organization || ""}
                onChange={(e) => setProfile({ ...profile, organization: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Job Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={profile.job_title || ""}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    job_title: e.target.value,
                  })
                }
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Job Function</label>
              <select
                value={profile.job_function_id || ""}
                onChange={(e) => setProfile({ ...profile, job_function_id: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a job function</option>
                {jobFunctions.map((jf) => (
                  <option key={jf.id} value={jf.id}>
                    {jf.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Industry</label>
              <select
                value={profile.industry_id || ""}
                onChange={(e) => setProfile({ ...profile, industry_id: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an industry</option>
                {industries.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={profile.city || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s'-]/g, "")
                  setProfile({ ...profile, city: value })
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <select
                value={profile.country_id || ""}
                onChange={(e) => setProfile({ ...profile, country_id: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
              <div className="mt-2">
                <Link to="/settings" className="text-sm text-blue-600 hover:text-blue-700">
                  Change email
                </Link>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                pattern="\d*"
                value={profile.phone || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d]/g, "")
                  setProfile({ ...profile, phone: value })
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phone number"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Social Links</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700">Facebook</label>
                <input
                  type="text"
                  value={profile.social_links.facebook || ""}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      social_links: { ...profile.social_links, facebook: e.target.value },
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://facebook.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Twitter</label>
                <input
                  type="text"
                  value={profile.social_links.twitter || ""}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      social_links: { ...profile.social_links, twitter: e.target.value },
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://twitter.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
                <input
                  type="text"
                  value={profile.social_links.linkedin || ""}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      social_links: { ...profile.social_links, linkedin: e.target.value },
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Your Interests</h3>
              <div className="flex flex-wrap gap-2">
                {sectors.map((sector) => (
                  <Button
                    key={sector.id}
                    onClick={() => handleSectorChange(sector.id.toString())}
                    variant={userTaxonomies.sectors.includes(sector.id.toString()) ? "filled" : "outline"}
                    size="small"
                    className="rounded-full"
                  >
                    {sector.name}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sub Interests</h3>
              {sectors.map((sector) => (
                <div
                  key={sector.id}
                  className={
                    userTaxonomies.sectors.includes(sector.id.toString()) ? "mb-6" : "hidden"
                  }
                >
                  <h4 className="text-base font-medium text-gray-700 mb-2">
                    {sector.name}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {allSubsectors
                      .filter((subsector) => subsector.sector_id === sector.id)
                      .map((subsector) => (
                        <Button
                          key={subsector.id.toString()}
                          onClick={() => handleSubsectorChange(subsector.id.toString())}
                          variant={
                            userTaxonomies.subsectors.includes(subsector.id.toString())
                              ? "filled"
                              : "outline"
                          }
                          size="small"
                          className="rounded-full"
                        >
                          {subsector.name}
                        </Button>
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                You have experience with
              </h3>

              <div className="mb-6">
                <h4 className="text-base font-medium text-gray-700 mb-2">Platforms</h4>
                <div className="flex flex-wrap gap-2">
                  {platforms.map((platform) => (
                    <Button
                      key={platform.id}
                      onClick={() => handlePlatformChange(platform.id.toString())}
                      variant={
                        userTaxonomies.platforms.includes(platform.id.toString())
                          ? "filled"
                          : "outline"
                      }
                      size="small"
                      className="rounded-full"
                    >
                      {platform.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-base font-medium text-gray-700 mb-2">Tools</h4>
                <div className="flex flex-wrap gap-2">
                  {tools.map((tool) => (
                    <Button
                      key={tool.id}
                      onClick={() => handleToolChange(tool.id.toString())}
                      variant={
                        userTaxonomies.tools.includes(tool.id.toString())
                          ? "filled"
                          : "outline"
                      }
                      size="small"
                      className="rounded-full"
                    >
                      {tool.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-base font-medium text-gray-700 mb-2">
                  Programming Languages
                </h4>
                <div className="flex flex-wrap gap-2">
                  {programmingLanguages.map((lang) => (
                    <Button
                      key={lang.id}
                      onClick={() => handleProgrammingLanguageChange(lang.id.toString())}
                      variant={
                        userTaxonomies.programming_languages.includes(lang.id.toString())
                          ? "filled"
                          : "outline"
                      }
                      size="small"
                      className="rounded-full"
                    >
                      {lang.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseCard>
  )
}
